import {
	applyStockSendDescriptionToLastItem,
	transformWarehouseStockToItems,
} from './warehouseSendDataTransformer';

export function stockSendSlotKey(slot) {
	return `${String(slot.warehouse_id)}__${String(slot.residue_id)}`;
}

function mergeUniqueIntakeIds(a = [], b = []) {
	return [
		...new Set([
			...(Array.isArray(a) ? a : []),
			...(Array.isArray(b) ? b : []),
		]),
	];
}

/**
 * Agrupa slots del modal que comparten bodega + residuo (suma kg a enviar).
 */
export function aggregateStockSendSlots(slots) {
	if (!Array.isArray(slots) || slots.length === 0) {
		return [];
	}
	const map = new Map();
	for (const s of slots) {
		const k = stockSendSlotKey(s);
		const kgSend = Number(s.kilograms_to_send);
		const kgTotal = Number(s.kilograms_total);
		if (!map.has(k)) {
			map.set(k, {
				_is_warehouse_stock_send: true,
				warehouse_id: s.warehouse_id,
				residue_id: s.residue_id,
				warehouse: s.warehouse,
				residue: s.residue,
				kilograms_to_send: Number.isFinite(kgSend) ? kgSend : 0,
				kilograms_total: Number.isFinite(kgTotal) ? kgTotal : 0,
				intake_ids: [...(s.intake_ids || [])],
			});
		} else {
			const prev = map.get(k);
			prev.kilograms_to_send += Number.isFinite(kgSend) ? kgSend : 0;
			if (Number.isFinite(kgTotal)) {
				prev.kilograms_total = Math.max(
					Number(prev.kilograms_total) || 0,
					kgTotal,
				);
			}
			prev.intake_ids = mergeUniqueIntakeIds(prev.intake_ids, s.intake_ids);
			if (s.warehouse) prev.warehouse = s.warehouse;
			if (s.residue) prev.residue = s.residue;
		}
	}
	return [...map.values()];
}

function renumberLineNumbers(items) {
	return items.map((item, i) => ({ ...item, NroLinDet: i + 1 }));
}

function itemStockSendKey(item) {
	const v = item?._module_vars;
	if (!v?._is_warehouse_stock_send) return null;
	return `${String(v.warehouse_id)}__${String(v.residue_id)}`;
}

/**
 * Fusiona slots de stock (`_is_warehouse_stock_send`) en los ítems del generador.
 * Solo participan líneas con `_module_vars._is_warehouse_stock_send` para la clave
 * `(warehouse_id, residue_id)`; el resto se mantiene en el mismo orden relativo.
 *
 * @returns {{ items: Array, stats: { merged: number, added: number } }}
 *
 * Pruebas manuales sugeridas:
 * - Generador envío bodega peligrosos: abrir picker, confirmar un slot → aparece línea con kg correctos.
 * - Repetir el mismo par bodega+residuo → `QtyItem` y `_module_vars.kilograms_to_send` deben sumarse.
 * - Prefill desde SendGuide sin usar el picker → ítems existentes no deben perderse al abrir/cerrar el modal sin confirmar.
 */
export function mergeStockSendGuideItems(
	existingItems,
	slotsFromModal,
	options = {},
) {
	const existing = Array.isArray(existingItems) ? [...existingItems] : [];
	const aggregated = aggregateStockSendSlots(slotsFromModal);

	if (aggregated.length === 0) {
		return {
			items: applyStockSendDescriptionToLastItem(
				renumberLineNumbers(existing),
				options,
			),
			stats: { merged: 0, added: 0 },
		};
	}

	const incomingByKey = new Map(
		aggregated.map(s => [stockSendSlotKey(s), s]),
	);

	let mergedCount = 0;
	const next = [];

	for (const item of existing) {
		const key = itemStockSendKey(item);
		if (key && incomingByKey.has(key)) {
			const inc = incomingByKey.get(key);
			const mv = item._module_vars || {};
			const mergedSlot = {
				_is_warehouse_stock_send: true,
				warehouse_id: inc.warehouse_id,
				residue_id: inc.residue_id,
				warehouse: inc.warehouse ?? mv.warehouse,
				residue: inc.residue ?? mv.residue,
				kilograms_to_send:
					Number(mv.kilograms_to_send || 0) +
					Number(inc.kilograms_to_send || 0),
				kilograms_total: Math.max(
					Number(mv.kilograms_total || 0),
					Number(inc.kilograms_total || 0),
				),
				intake_ids: mergeUniqueIntakeIds(mv.intake_ids, inc.intake_ids),
				warehouse_name:
					inc.warehouse?.name ||
					inc.warehouse_name ||
					mv.warehouse_name ||
					'',
			};
			const [line] = transformWarehouseStockToItems([mergedSlot]);
			next.push(line);
			incomingByKey.delete(key);
			mergedCount += 1;
		} else {
			next.push(item);
		}
	}

	let addedCount = 0;
	for (const slot of incomingByKey.values()) {
		const [line] = transformWarehouseStockToItems([slot]);
		next.push(line);
		addedCount += 1;
	}

	return {
		items: applyStockSendDescriptionToLastItem(renumberLineNumbers(next), options),
		stats: { merged: mergedCount, added: addedCount },
	};
}
